import { Component, Inject, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { LoaderService } from "../../services/loader.service";
import { DeliveryType } from "src/interfaces/delivery-type";
import { Address } from "src/interfaces/address";
import { Observable } from 'rxjs';
import { ShippingService, WaybillResponse } from 'src/core/services/shipping.service';
import { MatStepper } from '@angular/material/stepper';
import {
  WaybillPayload,
  sundryChargeTypes,
  serviceTypes,
  instructionCodes,
  Parcel,
  ParcelCapturePayload,
} from "src/interfaces/waybill";
import { environment } from "src/environments/environment";
import { EditOrderFormData, Order } from 'src/interfaces/order';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { ShippingDetails } from 'src/interfaces/shipping-details';
import { OrderItem } from 'src/interfaces/order-item';
import { IndividualService } from 'src/core/services/individual.service';
import { CompanyService } from 'src/core/services/company.service';
import { ActivatedRoute } from '@angular/router';
import { SageService } from 'src/core/services/sage.service';
import { sageSyncLabelFromOrder } from 'src/interfaces/sage-sync-status';

export interface DialogData {
  order: Order;
  title: string;
  message: string;
}

@Component({
  selector: 'core-shipping-form',
  templateUrl: './shipping-form.component.html',
  styleUrls: ['./shipping-form.component.scss']
})
export class ShippingFormComponent implements OnInit {



  senderAddressForm = this._formBuilder.group({
    unit: [''],
    street: ['', Validators.compose([Validators.maxLength(30), Validators.required])],
    city: ['', Validators.compose([Validators.maxLength(30)])],
    surburb: ['', Validators.compose([Validators.maxLength(30), Validators.required])],
    postalCode: ['', Validators.compose([Validators.required, Validators.pattern(/^[0-9]{4,5}$/)])],
    buildingNumber: [''],
    buildingComplexName: ['', Validators.compose([Validators.maxLength(30), Validators.required])],
    workTel: ['', Validators.compose([Validators.required, Validators.pattern(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)])],
    contactPerson: ['', Validators.compose([Validators.maxLength(40), Validators.required])]
  });


  deliveryAddressForm = this._formBuilder.group({
    unit: [''],
    street: ['', Validators.compose([Validators.maxLength(30), Validators.required])],
    city: ['', Validators.compose([Validators.maxLength(30)])],
    surburb: ['', Validators.compose([Validators.maxLength(30), Validators.required])],
    postalCode: ['', Validators.compose([Validators.required, Validators.pattern(/^[0-9]{4,5}$/)])],
    buildingNumber: [''],
    buildingComplexName: ['', Validators.compose([Validators.maxLength(30)])],
    cell: ['', Validators.compose([Validators.required, Validators.pattern(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)])],
    company: ['', Validators.compose([Validators.maxLength(50)])],
    contactPerson: ['', Validators.required]
  });


  serviceTypeForm = this._formBuilder.group({
    type: ['', Validators.required]
  })




  deliveryTypes: DeliveryType[] = [];
  addresses: Address[] = [];
  selectedAddress: Address;
  filteredAddresses: Observable<Address[]>;
  isLoading: boolean = false;
  individualId: number;
  insured: boolean = false;
  sundryChargeTypes = sundryChargeTypes;
  serviceTypes = serviceTypes;
  instructionCodes = instructionCodes;
  parcels: Parcel[] = [];
  waybillPayload: WaybillPayload | null = null;
  order: Order | null = null;
  readonly sageUiEnabled = environment.sageUiEnabled;
  magaliesConfig = environment.waybill.magaliesConfig;
  @ViewChild('stepper') private _stepper: MatStepper;

  stepValid: boolean = false;
  hideNext: boolean = false;

  waybillNo: string;
  canCompleteWaybill: boolean = false;
  labelLinks: string[] = [];
  waybillCompleted: boolean = false;
  constructor(
    private _formBuilder: FormBuilder,
    private _loaderService: LoaderService,
    private _shippingService: ShippingService,
    public _dialogRef: MatDialogRef<ShippingFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private _prescriptionService: PrescriptionService,
    private _individualService: IndividualService,
    private _companyService: CompanyService,
    private _route: ActivatedRoute,
    private _sageService: SageService
  ) { }

  ngOnInit() {
    this
      ._loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this.order = this.data?.order ?? this._route?.snapshot?.data?.order ?? null;

    if (this.order) {
      this.updateView(this.order);
    }
  }

  getSageSyncLabel(): string {
    return sageSyncLabelFromOrder(this.order);
  }

  async syncOrderToSage(): Promise<void> {
    if (!this.order) return;
    const ok = await this._sageService.syncOrderToSage(this.order.id);
    if (ok && this.order) {
      const u = await this._prescriptionService.getOrder(this.order.id);
      if (u) this.order = u;
    }
  }

  updateView(order: Order) {
    this._shippingService.generateWaybillNumber().then(waybillNo => {
      this.waybillNo = `MGP${String(waybillNo.data.waybill_number).padStart(7, '0')}`
    });
    this.senderAddressForm
      .patchValue({
        unit: this.magaliesConfig.unit,
        street: this.magaliesConfig.street,
        city: this.magaliesConfig.city,
        surburb: this.magaliesConfig.surburb,
        postalCode: this.magaliesConfig.postalCode,
        buildingNumber: this.magaliesConfig.buildingNumber,
        buildingComplexName: this.magaliesConfig.buildingComplexName,
        cell: this.magaliesConfig.cell,
        company: this.magaliesConfig.company,
        contactPerson: this.magaliesConfig.contactPerson,
        workTel: this.magaliesConfig.workTel
      });

    this.deliveryAddressForm
      .patchValue({
        street: this.address ? this.address.address_1 : "",
        city: this.address ? this.address.city : "",
        surburb: this.address ? this.address.address_2 : "",
        postalCode: this.address ? this.address.postcode : "",
        buildingNumber: this.address ? this.address.building_apt_number : "",
        cell: this.cellNo,
        company: this.companyName,
        contactPerson: this.contactPerson
      })

  }

  get address(): Address | null {
    if (!this.order) return null;
    return this.order.shipping_details.address;
  }

  get contactPerson(): string | undefined {

    if (!this.order) return undefined;

    if (this.order.client)
      return "";

    if (this.order.doctor)
      return `${this.order.doctor.first_name} ${this.order.doctor.last_name}`;

    return this.order.patient ?
      `${this.order.patient.first_name} ${this.order.patient.last_name}` : undefined;

  }


  get companyName(): string | undefined {

    if (!this.order) return undefined;

    if (this.order.patient) return undefined;

    if (this.order.doctor) return this.order
      .doctor
      .company ? this.order.doctor.company.name : undefined;

    if (this.order.client) return this.order
      .client ?
      this.order.client.name : undefined;

    return undefined;
  }

  get altCellNo(): string {
    if (!this.order) return "1234567890";

    if (this.order.patient) return this.order.patient.phone_number_alt;

    if (this.order.doctor) return this.order
      .doctor
      .company ? (this.order
        .doctor
        .company
        .phone_number_alt ? this.order
          .doctor
          .company
        .phone_number_alt : "1234567890") : this.order
          .doctor
      .phone_number_alt;

    if (this.order.client) return this.order
      .client ?
      (this.order
        .client
        .phone_number_alt ? this.order
          .client
        .phone_number_alt : "") : ""

    return "1234567890";
  }

  get cellNo(): string {

    if (!this.order) return "";

    if (this.order.patient) return this.order.patient.phone_number;

    if (this.order.doctor) return this.order
      .doctor
      .company ? (this.order
        .doctor
        .company
        .phone_number ? this.order
          .doctor
          .company
        .phone_number : "") : this.order
          .doctor
      .phone_number;

    if (this.order.client) return this.order
      .client ? (this.order
        .client
        .phone_number ? this.order
          .client
        .phone_number : "") : "";

    return "";
  }

  get recEmailAddress(): string | undefined {

    if (!this.order) return undefined;

    if (this.order.patient) return this.order.patient.email;
    if (this.order.client) return this.order.client.email;
    if (this.order.doctor) return this.order.doctor.email;

    return undefined;
  }



  addParcel(parcel: Parcel) {

    parcel.parcelNo = `${this.waybillNo}_${this.parcels.length + 1}`;
    this.parcels.push(parcel);
    this.parcels = [
      ...this.parcels
    ];

    this.stepValid = true;
  }

  deleteParcel(parcel: Parcel) {

    const index = this.parcels.indexOf(parcel);

    if (index > -1)
      this.parcels.splice(index, 1);
    this.parcels = [
      ...this.parcels
    ];
  }

  get totalMass(): number {
    return this.parcels
      .map(parcel => parcel.mass)
      .reduce((a, b) => a + b, 0);
  }

  get totalQuantity(): number {
    return this.parcels
      .map(parcel => parcel.parcelCount)
      .reduce((a, b) => a + b, 0)
  }

  get waybillWasCaptured(): boolean {
    return this.order ? (this.order
      .shipping_details.waybill_number ? true : false)
      : false;
  }

  get orderWaybillNumber(): string | null {
    return this.order ? (this.order
      .shipping_details
      .waybill_number ? this.order
        .shipping_details
      .waybill_number : null)
      : null;
  }


  prepareWaybill() {


    this.waybillPayload = <WaybillPayload>{

      // Magalies pharma details 
      waybillNo: this.waybillNo,
      serviceType: this.serviceTypeForm.value.type,
      sendAccNo: this.magaliesConfig.sendAccNo,
      sendSite: this.magaliesConfig.sendSite,
      sendCompany: this.magaliesConfig.sendCompany,
      sendAdd1: this.senderAddressForm.value.unit,
      sendAdd2: this.senderAddressForm.value.street,
      sendAdd3: this.senderAddressForm.value.city,
      sendAdd4: this.senderAddressForm.value.surburb,
      sendAdd5: this.senderAddressForm.value.postalCode,
      sendAdd6: this.senderAddressForm.value.buildingNumber,
      sendAdd7: this.senderAddressForm.value.buildingComplexName,
      sendContactPerson: this.senderAddressForm.value.contactPerson,
      sendLongitude: this.magaliesConfig.longitude,
      sendLatitude: this.magaliesConfig.latitude,
      sendW3w: this.magaliesConfig.w3w,
      sendHomeTel: this.magaliesConfig.homeTel,
      sendWorkTel: this.magaliesConfig.workTel,
      sendCell: this.magaliesConfig.cell,

      // Drop off point details 
      recCompany: this.deliveryAddressForm.value.company,
      recEmailAddress: this.recEmailAddress ? this.recEmailAddress : undefined,
      recAdd1: this.deliveryAddressForm.value.unit,
      recAdd2: this.deliveryAddressForm.value.street, 
      recAdd3: this.deliveryAddressForm.value.city,
      recAdd4: this.deliveryAddressForm.value.surburb,
      recAdd5: this.deliveryAddressForm.value.postalCode,
      recAdd6: this.deliveryAddressForm.value.buildingNumber,
      recAdd7: this.deliveryAddressForm.value.buildingComplexName,
      recContactPerson: this.deliveryAddressForm.value.contactPerson,
      recCell: this.deliveryAddressForm.value.cell,
      recLongitude: undefined,
      recLatitude: undefined,
      recW3w: undefined,
      recHomeTel: undefined,
      recWorkTel: undefined,

      specialInstructions: undefined,
      totQTY: this.parcels.length,
      totMass: undefined,
      insurance: undefined,
      insuranceValue: undefined,
      customerRef: undefined,
      storeCode: this.magaliesConfig.storeCode,
      securityStamp: null,
      requiredDocs: undefined,
      waybillInstructions: undefined,
      instructionCode: undefined,
      isSecureDelivery: undefined,
      verificationNumbers: undefined,
      generateSecurePin: undefined,
      collectionNo: undefined,
      invoiceRef: undefined,
      Misc: undefined,
      parcels: this.parcels,
      documents: undefined,
      completeWaybillAfterSave: true,
      generateLabel: true,
      bookedDate: undefined,
      SundryCharges: undefined
    };

    this._stepper.next();
  }

  async captureWaybill() {
    const response: WaybillResponse = await this._shippingService
      .captureWayBill(this.waybillPayload);

    const { success, data } = response;

    this.labelLinks = data ? data : [];

    // edit order 
    if (success && this.order) {
      const success: boolean = await this.editOrder(this.order);

      this.waybillCompleted = success; // Positive :)

      if (success) this._stepper.next();
    }
  }



  async captureParcel(parcel: Parcel) {
    const payload: ParcelCapturePayload = {
      ...parcel
    }
    const success: boolean = await this._shippingService
      .captureParcel(payload);

    if (success) {
      this.parcels.push(parcel);
      this.parcels = [
        ...this.parcels
      ];
    }
  }




  async editOrder(order: Order): Promise<boolean> {

    const shippingDetails: ShippingDetails = order.shipping_details;
    const orderItems: OrderItem[] = this._prescriptionService.getOrderItems(order);

    const medicine = orderItems.map(item => (
      {
        id: item.id ? item.id : undefined, // a new order item has an id of undefined
        quantity: item.quantity,
        medicine_dosage_package_type_id: item.medicineDosagePackageType.id,
        order_item_status_id: item.status ? item.status.id : null,
        deleted_at: item.deleted_at ? item.deleted_at : undefined
      }));

    const payload: EditOrderFormData = {
      id: order.id,
      barcode: order.barcode,
      created_at: order.created_at,
      updated_at: order.updated_at,
      deleted_at: order.deleted_at,
      order_status_id: order.order_status_id,
      client_id: order.client_id ? order.client_id : undefined,
      doctor_id: order.doctor_id,
      patient_id: order.patient_id,
      shipping_details_id: order.shipping_details_id,
      parent_order_id: order.parent_order_id,
      doctor: order.doctor ? this._individualService.constructIndividualEditPayload(order.doctor) : undefined,
      client: order.client ? this._companyService.constructionCompanyEditpayload(order.client, null) : undefined,
      status: order.status,
      patient: order.patient ? this._individualService.constructIndividualEditPayload(order.patient) : undefined,
      shipping_details: {
        id: shippingDetails.id,
        waybill_number: this.waybillNo,
        address_id: shippingDetails.address_id,
        delivery_type_id: shippingDetails.delivery_type_id,
        address: shippingDetails.address,
        waybill_data: this.labelLinks,
      },
      medicine,
      prescriber_id: order.prescriber_id ? order.prescriber_id : undefined,
      prescriber: order.prescriber ? this._individualService.constructIndividualEditPayload(order.prescriber) : undefined,
    }

    const success = await this._prescriptionService
      .editOrder(payload, true);

    return success;
  }


  viewLabel(link: string) {
    window.open(link, '_blank')?.focus();
  }




}
